<template>
  <div class="container py-4">
    <!-- Loading State -->
    <div v-if="loading" class="text-center my-5">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Laden...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <font-awesome-icon icon="exclamation-triangle" class="me-2" />
      {{ error }}
    </div>

    <!-- Receipt Details -->
    <div v-else-if="receipt">
      <!-- Header -->
          <div class="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 class="mb-1">
                <font-awesome-icon icon="receipt" class="me-2" />
                {{ receipt.title }}
              </h2>
              <p class="text-muted mb-0">
                {{ isInvoiceReceipt ? 'Rechnungs' : 'Quittungs' }}-Nr: {{ receipt.receiptNumber }}
              </p>
            </div>
            <div class="d-flex gap-2">
              <button
                class="btn btn-outline-primary"
                @click="recreateReceipt"
                v-if="(user?.roles.some(role => role.teamId === receipt.teamId) || receipt.userId === getCurrentUserId()) && !(!receipt.teamTemplateId && receipt.teamId)"
              >
                <font-awesome-icon icon="redo" class="me-1" />
                {{ isInvoiceReceipt ? 'Rechnung' : 'Quittung' }} erneut erstellen
              </button>
              <button
                v-if="canDownloadPDF"
                class="btn btn-success"
                @click="downloadPDF"
              >
            <font-awesome-icon icon="download" class="me-1" />
            PDF herunterladen
          </button>
          <BackButton fallback="receipts" v-if="user?.roles.some(role => role.teamId === receipt.teamId)" />
        </div>
      </div>

      <!-- Receipt Card -->
      <div class="row">
        <div class="col-lg-8">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center">
              <h4 class="mb-0 text-uppercase">{{ documentLabel.toUpperCase() }}</h4>
              <span 
                :class="[
                  'badge',
                  receipt.status === 'completed' ? 'bg-success' : 
                  receipt.status === 'draft' ? 'bg-secondary' : 
                  receipt.status === 'canceled' ? 'bg-warning' :
                  receipt.status === 'rejected' ? 'bg-danger' :
                  'bg-warning'
                ]"
              >
                {{ getStatusLabel(receipt.status) }}
              </span>
            </div>
            <div class="card-body">
              <div
                v-if="isInvoiceReceipt && receipt.status === 'canceled' && hasCorrection"
                class="alert alert-info d-flex align-items-start mb-4"
              >
                <font-awesome-icon icon="info-circle" class="me-2 mt-1" />
                <div>
                  <strong>Rechnung storniert.</strong>
                  Eine Rechnungskorrektur mit der Nummer
                  <span class="fw-semibold">{{ correctionNumber || 'unbekannt' }}</span>
                  steht zum Download bereit.
                </div>
              </div>
              <div class="row g-4">
                <!-- Basic Info -->
                <div class="col-md-6">
                  <h6 class="text-muted mb-3">GRUNDDATEN</h6>
                  <div class="mb-3" v-if="receipt.title && !receipt.title.includes(receipt.receiptNumber)">
                    <strong>Titel:</strong><br>
                    <span>{{ receipt.title }}</span>
                  </div>
                  <div class="mb-3">
                    <strong>Betrag:</strong><br>
                    <span class="h5 text-primary">{{ formatAmount(receipt.amount) }} €</span>
                  </div>
                </div>

                <!-- Participants -->
                <div class="col-md-6">
                  <h6 class="text-muted mb-3">BETEILIGTE</h6>
                  <div class="mb-3" v-if="receipt.teamId">
                    <strong>Bereich:</strong><br>
                    <span>{{ receipt?.team?.name || receipt.teamName }}</span>  
                  </div>
                  <div class="mb-3" v-else>
                    <strong>Persönliche Quittung</strong><br>
                  </div>
                </div>

                <!-- Tax Info -->
                <div v-if="receipt.taxRate && receipt.taxRate !== '0'" class="col-12">
                  <h6 class="text-muted mb-3">STEUERINFORMATIONEN</h6>
                  <div class="row">
                    <div class="col-md-4">
                      <strong>MwSt-Satz:</strong><br>
                      <span>{{ receipt.taxRate }}%</span>
                    </div>
                    <div class="col-md-4">
                      <strong>Nettobetrag:</strong><br>
                      <span>{{ calculateNetAmount(receipt.amount, receipt.taxRate) }} €</span>
                    </div>
                    <div class="col-md-4">
                      <strong>MwSt-Betrag:</strong><br>
                      <span>{{ calculateTaxAmount(receipt.amount, receipt.taxRate) }} €</span>
                    </div>
                  </div>
                </div>

                <!-- Additional Data mit erweiterten Anhängen -->
                <div v-if="receipt.data && Object.keys(receipt.data).length > 0 || receipt.attachments && Object.keys(receipt.attachments).length > 0" class="col-12">
                 
                  
                  <!-- Personal Fields -->
                  <div v-if="personalGroupedFields.length > 0 || personalAdditionalFields.length > 0" class="mb-3">
                    <h6 class="text-primary mb-2">
                      <font-awesome-icon :icon="getCategoryIcon('personal')" class="me-2" />
                      {{ getCategoryLabel('personal') }}
                    </h6>
                    <div
                      v-for="group in personalGroupedFields"
                      :key="`personal-group-${group.id}`"
                      class="wizard-field-group mb-4"
                      :style="getGroupBlockStyle(group.color)"
                    >
                      <div class="d-flex align-items-center mb-2">
                        <span
                          class="badge rounded-pill fw-semibold"
                          :class="getContrastTextClass(group.color || '#E9ECEF')"
                          :style="{ backgroundColor: group.color || '#E9ECEF' }"
                        >
                          {{ group.name }}
                        </span>
                      </div>
                      <div class="row g-3">
                        <div
                          v-for="item in group.fields"
                          :key="`${group.id}-${item.key}`"
                          class="col-md-6"
                        >
                          <strong class="text-dark">{{ item.fieldInfo.value.label || item.key }}:</strong><br>
                          <span class="text-dark">{{ item.display }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="row g-3" v-if="personalAdditionalFields.length > 0">
                      <div
                        v-for="item in personalAdditionalFields"
                        :key="item.key"
                        class="col-md-6"
                      >
                        <strong class="text-dark">{{ item.fieldInfo.value.label || item.key }}:</strong><br>
                        <span class="text-dark">{{ item.display }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Receipt Fields -->
                  <div v-if="hasReceiptSection" class="mb-3">
                    <h6 class="text-success mb-2">
                      <font-awesome-icon :icon="getCategoryIcon('receipt')" class="me-2" />
                      {{ getCategoryLabel('receipt') }}
                    </h6>
                    <div
                      v-for="group in receiptGroupedFields"
                      :key="`receipt-group-${group.id}`"
                      class="wizard-field-group mb-4"
                      :style="getGroupBlockStyle(group.color)"
                    >
                      <div class="d-flex align-items-center mb-2">
                        <span
                          class="badge rounded-pill fw-semibold"
                          :class="getContrastTextClass(group.color || '#E9ECEF')"
                          :style="{ backgroundColor: group.color || '#E9ECEF' }"
                        >
                          {{ group.name }}
                        </span>
                      </div>
                      <div class="row g-3">
                        <div
                          v-for="item in group.fields"
                          :key="`${group.id}-${item.key}`"
                          class="col-md-6"
                        >
                          <strong class="text-dark">{{ item.fieldInfo.value.label || item.key }}:</strong><br>
                          <span class="text-dark">{{ item.display }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="row g-3" v-if="receiptStandaloneFields.length > 0">
                      <div
                        v-for="item in receiptStandaloneFields"
                        :key="item.key"
                        class="col-md-6"
                      >
                        <strong class="text-dark">{{ item.fieldInfo.value.label || item.key }}:</strong><br>
                        <span class="text-dark">{{ item.display }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Financial Fields -->
                  <div v-if="hasFinancialSection" class="mb-3">
                    <h6 class="text-warning mb-2">
                      <font-awesome-icon :icon="getCategoryIcon('financial')" class="me-2" />
                      {{ getCategoryLabel('financial') }}
                    </h6>
                    <div
                      v-for="group in financialGroupedFields"
                      :key="`financial-group-${group.id}`"
                      class="wizard-field-group mb-4"
                      :style="getGroupBlockStyle(group.color)"
                    >
                      <div class="d-flex align-items-center mb-2">
                        <span
                          class="badge rounded-pill fw-semibold"
                          :class="getContrastTextClass(group.color || '#E9ECEF')"
                          :style="{ backgroundColor: group.color || '#E9ECEF' }"
                        >
                          {{ group.name }}
                        </span>
                      </div>
                      <div class="row g-3">
                        <div
                          v-for="item in group.fields"
                          :key="`${group.id}-${item.key}`"
                          class="col-md-6"
                        >
                          <strong class="text-dark">{{ item.fieldInfo.value.label || item.key }}:</strong><br>
                          <span class="text-dark">{{ item.display }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="row g-3" v-if="financialStandaloneFields.length > 0">
                      <div
                        v-for="item in financialStandaloneFields"
                        :key="item.key"
                        class="col-md-6"
                      >
                        <strong class="text-dark">{{ item.fieldInfo.value.label || item.key }}:</strong><br>
                        <span class="text-dark">{{ item.display }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Combined Positions Table -->
                  <div v-if="hasCombinedRows" class="mb-3">
                    <h6 class="text-primary mb-2">
                      <font-awesome-icon icon="layer-group" class="me-2" />
                      Positionen
                    </h6>
                    <ReceiptBillingTable
                      :table-data="combinedTableDisplay"
                      empty-label="Keine Positionen vorhanden"
                    />
                  </div>
                  
                  <!-- Anhänge mit Download-Buttons -->
                  <div v-if="receipt.attachments && Object.keys(receipt.attachments).length > 0" class="mt-3">
                    <h6 class="text-info mb-2">
                      <font-awesome-icon icon="paperclip" class="me-2" />
                      Anhänge
                    </h6>
                    <div v-for="(attachments, fieldName) in receipt.attachments" :key="`attachment-${fieldName}`" class="mb-3">
                      <strong>{{ fieldName }}:</strong><br>
                      <div v-if="Array.isArray(attachments) && attachments.length > 0" class="mt-2">
                        <div 
                          v-for="(attachment, index) in attachments" 
                          :key="`${fieldName}-${index}`" 
                          class="d-flex align-items-center justify-content-between mb-2 p-2 border rounded bg-light"
                        >
                          <div class="d-flex align-items-center">
                            <font-awesome-icon 
                              :icon="getFileIcon(attachment.originalName)" 
                              class="me-2 text-primary"
                            />
                            <div>
                              <div class="fw-semibold">{{ attachment.originalName || `Anhang ${index + 1}` }}</div>
                              <small v-if="attachment.compressedSize" class="text-muted">
                                {{ formatFileSize(attachment.compressedSize) }}
                              </small>
                            </div>
                          </div>
                          <button
                            v-if="canDownloadAttachments"
                            class="btn btn-sm btn-outline-primary"
                            @click="downloadAttachment(receipt.id, fieldName, index, attachment.originalName)"
                            :disabled="downloadingAttachment === `${fieldName}-${index}`"
                            title="Anhang herunterladen"
                          >
                            <font-awesome-icon 
                              :icon="downloadingAttachment === `${fieldName}-${index}` ? 'spinner' : 'download'" 
                              :class="{ 'fa-spin': downloadingAttachment === `${fieldName}-${index}` }"
                              class="me-1" 
                            />
                            Herunterladen
                          </button>
                          <div v-else class="text-muted small">
                            <font-awesome-icon icon="ban" class="me-1" />
                            Download nicht verfügbar
                          </div>
                        </div>
                      </div>
                      <div v-else class="text-muted fst-italic">
                        Keine Anhänge vorhanden
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Begründung für Ablehnung/Stornierung -->
                <div v-if="receipt.rejectionReason && (receipt.status === 'rejected' || receipt.status === 'canceled')" class="col-12" >
                  <h6 class="text-muted mb-3" >
                    <font-awesome-icon 
                      :icon="receipt.status === 'rejected' ? 'ban' : 'times-circle'" 
                      class="me-2"
                      :class="receipt.status === 'rejected' ? 'text-danger' : 'text-warning'"
                    />
                    {{ receipt.status === 'rejected' ? 'ABLEHNUNGSGRUND' : 'STORNIERUNGSGRUND' }}
                  </h6>
                  <div class="alert" :class="receipt.status === 'rejected' ? 'alert-danger' : 'alert-gradient-warning'">
                    <p class="mb-0">{{ receipt.rejectionReason }}</p>
                  </div>
                </div>

                <!-- Notes -->
                <div v-if="receipt.notes" class="col-12">
                  <h6 class="text-muted mb-3">NOTIZEN</h6>
                  <p class="mb-0">{{ receipt.notes }}</p>
                </div>

                <!-- Signature -->
                <div v-if="receipt.signature" class="col-12">
                  <h6 class="text-muted mb-3">UNTERSCHRIFT</h6>
                  <div class="text-center">
                    <img 
                      :src="receipt.signature" 
                      alt="Unterschrift" 
                      class="img-fluid border rounded bg-white"
                      style="max-height: 150px; user-select: none; -webkit-user-select: none; -moz-user-select: none; -ms-user-select: none; pointer-events: none; -webkit-touch-callout: none; -webkit-user-drag: none; -khtml-user-select: none;"
                      draggable="false"
                      oncontextmenu="return false;"
                      onselectstart="return false;"
                      onmousedown="return false;"
                      @dragstart="(e) => e.preventDefault()"
                      @contextmenu="(e) => e.preventDefault()"
                      @selectstart="(e) => e.preventDefault()"
                      @mousedown="(e) => e.preventDefault()"
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="col-lg-4">
          <!-- Status Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">Status</h6>
            </div>
            <div class="card-body">
              <div class="mb-3">
                              <span 
                :class="[
                  'badge fs-6',
                  receipt.status === 'completed' ? 'bg-success' : 
                  receipt.status === 'draft' ? 'bg-secondary' : 
                  receipt.status === 'canceled' ? 'bg-warning' :
                  receipt.status === 'rejected' ? 'bg-danger' :
                  'bg-warning'
                ]"
              >
                {{ getStatusLabel(receipt.status) }}
              </span>
              </div>
              <div v-if="isRefundActive" class="mb-2">
                <strong>Bezahlstatus:</strong><br>
                <span 
                  :class="[
                    'badge',
                    receipt.payed === 'payed' ? 'bg-success' : 
                    receipt.payed === 'open' ? 'bg-danger' : 
                    'bg-primary'
                  ]"
                >
                  {{ getPaymentStatusLabel(receipt.payed) }}
                </span>
              </div>
              <div v-else-if="receipt.teamName" class="alert alert-warning mb-0">
                Erstattung nicht aktiviert
              </div>
              <div v-if="receipt.paymentDate && receipt.teamName">
                <strong>Bezahldatum:</strong><br>
                <span>{{ formatDate(receipt.paymentDate) }}</span>
              </div>
            </div>
          </div>

          <!-- Actions Card -->
          <div class="card mb-4">
            <div class="card-header">
              <h6 class="mb-0">Aktionen</h6>
            </div>
            <div class="card-body">
              <div class="d-grid gap-2">
                <button
                  v-if="canDownloadPDF"
                  class="btn btn-outline-success"
                  @click="downloadPDF"
                >
                  <font-awesome-icon icon="download" class="me-1" />
                  PDF herunterladen
                </button>
                <button
                  v-if="showCorrectionDownload"
                  class="btn btn-outline-primary"
                  @click="downloadCorrectionPDF"
                >
                  <font-awesome-icon icon="file-invoice" class="me-1" />
                  Korrekturrechnung herunterladen
                </button>
                <div v-else-if="receipt.pdfPath && (receipt.status === 'rejected' || receipt.status === 'canceled')" class="alert alert-gradient-warning">
                  <font-awesome-icon icon="exclamation-triangle" class="me-1" />
                  Download nicht möglich - {{ documentLabel }} wurde {{ receipt.status === 'rejected' ? 'abgelehnt' : 'storniert' }}
                </div>
                
                <!-- Reaktivieren Buttons -->
                <button
                  v-if="canReactivateCanceledReceipt"
                  class="btn btn-outline-success"
                  @click="reactivateCanceledReceipt"
                >
                  <font-awesome-icon icon="undo" class="me-1" />
                  Stornierung aufheben
                </button>
                
                <button
                  v-if="canReactivateRejectedReceipt"
                  class="btn btn-outline-success"
                  @click="reactivateRejectedReceipt"
                >
                  <font-awesome-icon icon="undo" class="me-1" />
                  Ablehnung aufheben
                </button>
                
                <button
                  v-if="receipt.status === 'draft'"
                  class="btn btn-outline-danger"
                  @click="deleteReceipt"
                >
                  <font-awesome-icon icon="trash" class="me-1" />
                  Löschen
                </button>
              </div>
            </div>
          </div>

          <!-- Gefahrenzone Card -->
          <div v-if="showDangerZone" class="card border-danger mb-4">
            <div 
              class="card-header bg-danger text-white" 
              style="cursor: pointer;"
              @click="toggleDangerZone"
            >
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <font-awesome-icon icon="exclamation-triangle" class="me-2" />
                  Gefahrenzone
                </h6>
                <font-awesome-icon 
                  :icon="isDangerZoneCollapsed ? 'chevron-down' : 'chevron-up'" 
                  class="ms-2"
                />
              </div>
            </div>
            <div v-show="!isDangerZoneCollapsed" class="card-body">
              <div class="d-grid gap-2">
                <!-- Stornieren/Löschen Button (nur für Benutzer, wenn nicht pending/payed) -->
                <button
                  v-if="canCancelReceipt"
                  class="btn btn-outline-warning"
                  @click="cancelReceipt"
                >
                  <font-awesome-icon icon="times-circle" class="me-1" />
                  {{ !receipt.teamId && !receipt.teamTemplateId ? (isInvoiceReceipt ? 'Rechnung löschen' : 'Quittung löschen') : (isInvoiceReceipt ? 'Rechnung stornieren' : 'Quittung stornieren') }}
                </button>
                
                <!-- Ablehnen Button (nur für Team-Admins) -->
                <button
                  v-if="canRejectReceipt"
                  class="btn btn-outline-danger"
                  @click="rejectReceipt"
                >
                  <font-awesome-icon icon="ban" class="me-1" />
                  {{ isInvoiceReceipt ? 'Rechnung' : 'Quittung' }} ablehnen
                </button>
              </div>
            </div>
          </div>

          <!-- Zahlungsstatus Card (nur für Bereich-Admins) -->
          <div v-if="isTeamAdmin && receipt.status === 'completed' && isRefundActive" class="card border-primary mb-5">
            <div 
              class="card-header bg-primary text-white" 
              style="cursor: pointer;"
              @click="togglePaymentStatusZone"
            >
              <div class="d-flex justify-content-between align-items-center">
                <h6 class="mb-0">
                  <font-awesome-icon icon="credit-card" class="me-2" />
                  Zahlungsstatus
                </h6>
                <font-awesome-icon 
                  :icon="isPaymentStatusZoneCollapsed ? 'chevron-down' : 'chevron-up'" 
                  class="ms-2"
                />
              </div>
            </div>
            <div v-show="!isPaymentStatusZoneCollapsed" class="card-body">
              <div class="d-grid gap-2">
                <button
                  class="btn btn-sm btn-outline-success"
                  @click="updatePaymentStatus('payed')"
                >
                  <font-awesome-icon icon="check" class="me-1" />
                  Bezahlt
                </button>
                
                <button
                  class="btn btn-sm btn-outline-warning"
                  @click="updatePaymentStatus('planed')"
                >
                  <font-awesome-icon icon="clock" class="me-1" />
                  Vorbereitet
                </button>
                
                <button
                  class="btn btn-sm btn-outline-danger"
                  @click="updatePaymentStatus('open')"
                >
                  <font-awesome-icon icon="times" class="me-1" />
                  Offen
                </button>
              </div>
            </div>
          </div>

          <!-- Meta Info -->
          <div class="card">
            <div class="card-header">
              <h6 class="mb-0">Details</h6>
            </div>
            <div class="card-body">
              <div class="small text-muted">
                <div class="mb-2">
                  <strong>Erstellt:</strong><br>
                  {{ formatDateTime(receipt.createdAt) }}
                </div>
                <div class="mb-2">
                  <strong>Benutzer-Name:</strong><br>
                  {{ receipt.userFirstName }} {{ receipt.userLastName }}
                </div>
                <div v-if="hasCorrection" class="mb-2">
                  <strong>Korrekturrechnung:</strong><br>
                  <span>{{ correctionNumber || 'vorhanden' }}</span>
                </div>
                <div v-if="receipt.recipientEmail"  >
                  <strong>Empfänger-E-Mail:</strong><br>
                  {{ receipt.recipientEmail }}
                </div>
                <div v-if="receipt.end2end && receipt.end2end.length > 0" class="mt-2">
                  <strong>End2End-ID:</strong><br>
                  <span>{{ receipt.end2end.join(', ') }}</span>
                </div>
                <div v-if="receipt.rejectionReason && receipt.status !== 'rejected'  && receipt.status !== 'canceled'" class="mt-2">
                  <strong>Hinweis:</strong><br>
                  <span>{{ receipt.rejectionReason }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { receiptsApi } from '@/api/receipts'
import ReceiptBillingTable from '@/components/receipt/ReceiptBillingTable.vue'
import { buildReceiptTables as buildReceiptTablesLocal, DEFAULT_COLUMNS } from '@/utils/billingTableBuilder'
import { buildCategoryInfo, getCategoryLabel as resolveCategoryLabel, getCategoryIcon as resolveCategoryIcon } from '@/utils/categoryInfo'
import { createReceiptFieldFormatter, groupFieldsByGroupId, getContrastTextClass, getGroupBlockStyle } from '@/utils/receiptFieldUtils'

import { showSuccess, showError, showPaymentDateModal } from '@/services/notification'
import { useSmartNavigation, FallbackRoutes } from '@/services/navigation'
import { useAuthStore } from '@/stores/auth'
import Swal from 'sweetalert2'

const route = useRoute()
const router = useRouter()
const { goBack } = useSmartNavigation()
const authStore = useAuthStore()
const {user } = storeToRefs(authStore)
const receipt = ref(null)
const categoryInfo = computed(() => buildCategoryInfo(receipt.value))
const getCategoryLabel = (category) => resolveCategoryLabel(category, categoryInfo.value)
const getCategoryIcon = (category) => resolveCategoryIcon(category, categoryInfo.value)
const createEmptyTable = () => ({
  columns: DEFAULT_COLUMNS,
  currencySymbol: '€',
  rows: [],
  summary: {
    net: 0,
    tax: 0,
    gross: 0,
    display: {
      net: '0.00€',
      tax: '0.00€',
      gross: '0.00€',
    },
  },
  taxBreakdown: [],
})

const receiptTables = ref({
  combinedTable: createEmptyTable(),
  fieldsUsedInCombined: [],
  repeaterTables: {},
  financialTable: createEmptyTable(),
})
const combinedTableDisplay = computed(() => receiptTables.value?.combinedTable || createEmptyTable())
const fieldsUsedInCombinedSet = computed(() => new Set(receiptTables.value?.fieldsUsedInCombined || []))
const hasCombinedRows = computed(() => combinedTableDisplay.value.rows.length > 0)

const normalizeReceiptTables = (tables) => ({
  combinedTable: tables?.combinedTable || createEmptyTable(),
  fieldsUsedInCombined: tables?.fieldsUsedInCombined || [],
  repeaterTables: tables?.repeaterTables || {},
  financialTable: tables?.financialTable || createEmptyTable(),
})
const loading = ref(false)
const error = ref(null)

const downloadingAttachment = ref(null)
const downloadingAllAttachments = ref(false)
const isDangerZoneCollapsed = ref(true)
const isPaymentStatusZoneCollapsed = ref(true)


const canCancelReceipt = computed(() => {
  if (!receipt.value) return false
  if (receipt.value.status === 'canceled') return false
  if (receipt.value.status === 'rejected') return false
  
  if (receipt.value.userId !== getCurrentUserId()) return false
  if(!receipt.value.teamId) return true
  
  if (receipt.value.payed === 'payed' || receipt.value.payed === 'planed') return false
 
  
  return true
})

const isInvoiceReceipt = computed(() => {
  if (!receipt.value) return false

  if (receipt.value.documentType) {
    return receipt.value.documentType === 'invoice'
  }

  const template = receipt.value.template
  const templateName = template?.template?.name || template?.name || null

  return templateName === 'invoice'
})

const documentLabel = computed(() => (isInvoiceReceipt.value ? 'Rechnung' : 'Quittung'))
const hasCorrection = computed(() => Boolean(receipt.value?.correction))
const correctionNumber = computed(() => receipt.value?.correction?.correctionNumber || null)
const showCorrectionDownload = computed(() =>
  isInvoiceReceipt.value && receipt.value?.status === 'canceled' && !!receipt.value?.correction?.pdfPath
)

const canRejectReceipt = computed(() => {
  if (!receipt.value) return false
  if (receipt.value.status === 'canceled') return false
  if (receipt.value.payed === 'payed') return false
  
  if (!receipt.value.teamId) return false
  
  
  if (!isTeamAdmin.value) return false
  if (receipt.value.userId === getCurrentUserId()) return false
  
  
  if (receipt.value.status === 'rejected') return false

  
  return true
})

const canDownloadPDF = computed(() => {
  if (!receipt.value) return false
  
  
  if (receipt.value.status === 'rejected') return false
  if (receipt.value.status === 'canceled' && !isInvoiceReceipt.value) return false
  
  
  if (!receipt.value.pdfPath) return false
  
  return true
})

const canDownloadAttachments = computed(() => {
  if (!receipt.value) return false
  
  
  if (receipt.value.status === 'rejected' || receipt.value.status === 'canceled') return false
  
  
  if (!hasAttachments.value) return false
  
  return true
})

const canReactivateCanceledReceipt = computed(() => {
  if (!receipt.value) return false
  
  
  if (receipt.value.userId !== getCurrentUserId()) return false
  
  
  if (receipt.value.status !== 'canceled') return false
  if (isInvoiceReceipt.value) return false
  
  return true
})

const canReactivateRejectedReceipt = computed(() => {
  if (!receipt.value) return false
  
  
  if (!receipt.value.teamId) return false
  
  
  if (!isTeamAdmin.value) return false
  
  
  if (receipt.value.status !== 'rejected') return false
  
  return true
})

const showDangerZone = computed(() => {
  return canCancelReceipt.value || canRejectReceipt.value
})

const isRefundActive = computed(() => {
  if (!receipt.value) return false
  return !!receipt.value.refundActivated
})


const isTeamAdmin = computed(() => {
  if (!receipt.value?.teamId || !authStore.user?.roles) return false
  
  const userRole = authStore.user.roles.find(role => {
    const roleTeamId = role.teamId || role.team?.id
    return roleTeamId === receipt.value.teamId
  })
  
  return userRole?.role === 'teamAdmin'
})


const getCurrentUserId = () => {
  return authStore.user?.id
}

const refreshReceiptTables = async (receiptPayload) => {
  const tables = await buildReceiptTablesLocal(receiptPayload, {
    includeRepeaterInFinancial: true,
  })
  receiptTables.value = normalizeReceiptTables(tables)
}


const loadReceipt = async () => {
  loading.value = true
  error.value = null
  
  try {
    const receiptId = route.params.id
    
    
    const authKey = receiptsApi.getReceiptAuthKey(receiptId)
    
    if (authKey) {
      
      try {
        receipt.value = await receiptsApi.getPublicReceipt(receiptId)
      } catch (publicErr) {
        if (publicErr.response?.status === 403) {
          
          receiptsApi.clearReceiptAuthKey(receiptId)
          router.push({ 
            name: 'receipt-access', 
            params: { id: receiptId },
            query: { redirect: route.fullPath }
          })
          return
        }
        throw publicErr
      }
    } else {
      
      try {
        receipt.value = await receiptsApi.getReceiptById(receiptId)
      } catch (authErr) {
        if (authErr.response?.status === 403) {
          
          router.push({ 
            name: 'receipt-access', 
            params: { id: receiptId },
            query: { redirect: route.fullPath }
          })
          return
        }
        throw authErr
      }
    }

    if (receipt.value) {
      await refreshReceiptTables(receipt.value)
    }
  } catch (err) {
    error.value = err.response?.data?.error || 'Fehler beim Laden der Quittung'
    console.error('Error loading receipt:', err)
  } finally {
    loading.value = false
  }
}


const downloadPDF = async () => {
  
  if (!canDownloadPDF.value) {
    const status = receipt.value?.status === 'rejected' ? 'abgelehnt' : 'storniert'
    showError(`Download nicht möglich - ${documentLabel.value} wurde ${status}`)
    return
  }
  
  try {
    const receiptId = receipt.value.id
    
    
    const authKey = receiptsApi.getReceiptAuthKey(receiptId)
    
    if (authKey) {
      
      await receiptsApi.downloadPublicReceiptPDF(receiptId)
    } else {
      
      await receiptsApi.downloadReceiptPDF(receiptId)
    }
    
    showSuccess(`${documentLabel.value} wird heruntergeladen`)
  } catch (err) {
    if (err.response?.status === 403) {
      
      router.push({ 
        name: 'receipt-access', 
        params: { id: receipt.value.id },
        query: { redirect: route.fullPath }
      })
      return
    }
    showError(err.response?.data?.error || 'Fehler beim Herunterladen der PDF')
  }
}

const downloadCorrectionPDF = async () => {
  if (!showCorrectionDownload.value || !receipt.value) return

  try {
    await receiptsApi.downloadReceiptCorrectionPDF(receipt.value.id)
    showSuccess('Rechnungskorrektur wird heruntergeladen')
  } catch (err) {
    if (err.response?.status === 403) {
      router.push({
        name: 'receipt-access',
        params: { id: receipt.value.id },
        query: { redirect: route.fullPath }
      })
      return
    }
    showError(err.response?.data?.error || 'Fehler beim Herunterladen der Korrekturrechnung')
  }
}


const handleGoBack = () => {
  router.push({
    name: 'receipts'
  })
}


const deleteReceipt = async () => {
  const result = await Swal.fire({
    title: `${documentLabel.value} löschen?`,
    text: `Möchtest du die ${documentLabel.value.toLowerCase()} "${receipt.value.title}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Ja, löschen',
    cancelButtonText: 'Abbrechen'
  })

  if (result.isConfirmed) {
    try {
      await receiptsApi.deleteReceipt(receipt.value.id)
      showSuccess(`${documentLabel.value} erfolgreich gelöscht`)
      handleGoBack()
    } catch (err) {
      showError(err.response?.data?.error || `Fehler beim Löschen der ${documentLabel.value}`)
    }
  }
}

const cancelReceipt = async () => {
  const isPersonalReceipt = !receipt.value.teamId && !receipt.value.teamTemplateId
  const actionText = isPersonalReceipt ? 'löschen' : 'stornieren'
  const actionTextCapitalized = isPersonalReceipt ? 'Löschen' : 'Stornieren'
  
  const { value: reason } = await Swal.fire({
    title: `${documentLabel.value} ${actionText}`,
    text: `Möchtest du diese ${documentLabel.value} ${actionText}?`,
    input: 'textarea',
    inputLabel: `Grund für das ${actionTextCapitalized} (optional)`,
    inputPlaceholder: `Gib hier den Grund für das ${actionTextCapitalized} ein...`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ffc107',
    cancelButtonColor: '#6c757d',
    confirmButtonText: actionTextCapitalized,
    cancelButtonText: 'Abbrechen',
    inputValidator: (value) => {
      
      return null
    }
  })

  if (reason !== undefined) { 
    try {
      await receiptsApi.cancelReceipt(receipt.value.id, reason)
      
      // Prüfe ob die Quittung keine teamId und keine teamTemplateId hat
      if (!receipt.value.teamId && !receipt.value.teamTemplateId) {
        // Quittung löschen und auf /receipts weiterleiten
        showSuccess(`${documentLabel.value} wurde gelöscht`)
        handleGoBack()
      } else {
        // Normale Stornierung - Quittung neu laden
        showSuccess(`${documentLabel.value} erfolgreich storniert`)
        await loadReceipt() 
      }
    } catch (err) {
      const errorAction = isPersonalReceipt ? 'Löschen' : 'Stornieren'
      showError(err.response?.data?.error || `Fehler beim ${errorAction} der ${documentLabel.value}`)
    }
  }
}

const rejectReceipt = async () => {
  const { value: reason } = await Swal.fire({
    title: 'Quittung ablehnen',
    text: 'Möchtest du diese Quittung ablehnen?',
    input: 'textarea',
    inputLabel: 'Grund für die Ablehnung (optional)',
    inputPlaceholder: 'Gib hier den Grund für die Ablehnung ein...',
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Ablehnen',
    cancelButtonText: 'Abbrechen',
    inputValidator: (value) => {
      
      return null
    }
  })

  if (reason !== undefined) { 
    try {
      await receiptsApi.rejectReceipt(receipt.value.id, reason)
      showSuccess('Quittung erfolgreich abgelehnt')
      await loadReceipt() 
    } catch (err) {
      showError(err.response?.data?.error || 'Fehler beim Ablehnen der Quittung')
    }
  }
}

const reactivateCanceledReceipt = async () => {
  const result = await Swal.fire({
    title: 'Stornierung aufheben?',
    text: `Möchtest du die Stornierung der Quittung "${receipt.value.title}" aufheben?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Ja, aufheben',
    cancelButtonText: 'Abbrechen'
  })

  if (result.isConfirmed) {
    try {
      await receiptsApi.reactivateCanceledReceipt(receipt.value.id)
      showSuccess('Stornierung erfolgreich aufgehoben')
      await loadReceipt() 
    } catch (err) {
      showError(err.response?.data?.error || 'Fehler beim Aufheben der Stornierung')
    }
  }
}

const reactivateRejectedReceipt = async () => {
  const result = await Swal.fire({
    title: 'Ablehnung aufheben?',
    text: `Möchtest du die Ablehnung der Quittung "${receipt.value.title}" aufheben?`,
    icon: 'question',
    showCancelButton: true,
    confirmButtonColor: '#28a745',
    cancelButtonColor: '#6c757d',
    confirmButtonText: 'Ja, aufheben',
    cancelButtonText: 'Abbrechen'
  })

  if (result.isConfirmed) {
    try {
      await receiptsApi.reactivateRejectedReceipt(receipt.value.id)
      showSuccess('Ablehnung erfolgreich aufgehoben')
      await loadReceipt() 
    } catch (err) {
      showError(err.response?.data?.error || 'Fehler beim Aufheben der Ablehnung')
    }
  }
}

const recreateReceipt = () => {
  if (!receipt.value) return

  router.push({
    name: 'receipt-create',
    query: { sourceReceiptId: receipt.value.id },
  })
}

const toggleDangerZone = () => {
  isDangerZoneCollapsed.value = !isDangerZoneCollapsed.value
}

const togglePaymentStatusZone = () => {
  isPaymentStatusZoneCollapsed.value = !isPaymentStatusZoneCollapsed.value
}


const formatAmount = (amount) => {
  return parseFloat(amount).toFixed(2)
}

const formatCurrency = (amount) => {
  return parseFloat(amount || 0).toFixed(2) + ' €'
}

const formatFinancialValue = (value, type, fieldName = '') => {
  // Handle empty values
  if (value === '' || value === null || value === undefined) {
    return '-'
  }
  
  // Handle simple number values (like totalAmount, calculated fields)
  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
    const numValue = parseFloat(value)
    if (type === 'total') {
      return formatCurrency(numValue)
    } else if (type === 'quantity') {
      return '1' // Simple fields have quantity 1
    } else if (type === 'unitPrice') {
      return formatCurrency(numValue) // For simple fields, the value is the unit price
    } else if (type === 'netto') {
      return formatCurrency(numValue) // For simple fields, netto = total
    }
    return '-'
  }
  
  if (Array.isArray(value)) {
    // Handle repeater fields
    return formatRepeaterFinancialValue(value, type)
  } else if (typeof value === 'object' && value !== null) {
    // Handle field objects with data property
    if (value.data !== undefined && value.data !== null && value.data !== '') {
      return formatFieldObjectValue(value, type, fieldName)
    }
    // Handle single financial field with quantity/unitPrice structure
    return formatSingleFinancialValue(value, type)
  }
  return '-'
}

const formatFieldObjectValue = (fieldObj, type, fieldName = '') => {
  const value = parseFloat(fieldObj.data) || 0
  const unit = fieldObj.unit || ''
  const pricePerUnit = fieldObj.pricePerUnit || null
  
  // Calculate the total amount: data * pricePerUnit
  const totalAmount = pricePerUnit ? value * pricePerUnit : value
  
  // Get tax rate from corresponding _taxRate field
  const taxRateFieldName = fieldName + '_taxRate'
  const taxRateField = receipt.value?.data?.[taxRateFieldName]
  const resolvedTaxRate = taxRateField?.resolvedTaxRate
  
  switch (type) {
    case 'quantity':
      if (unit && pricePerUnit && pricePerUnit > 0) {
        // Show the data value as quantity
        return value.toFixed(0)
      }
      return '' // Leave empty if no unit/pricePerUnit
    case 'unitPrice':
      if (pricePerUnit && pricePerUnit > 0) {
        // If there's a tax rate, show the gross price (pricePerUnit)
        if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
          return formatCurrency(pricePerUnit) + (unit ? '/' + unit : '')
        }
        // If no tax rate, show the net price
        return formatCurrency(pricePerUnit) + (unit ? '/' + unit : '')
      }
      // For simple fields without pricePerUnit, show the gross value
      if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
        return formatCurrency(value)
      }
      return formatCurrency(value)
    case 'taxRate':
      if (resolvedTaxRate) {
        return `${resolvedTaxRate.displayName} (${resolvedTaxRate.rate}%)`
      }
      return '0%'
    case 'netto':
      if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
        // Calculate netto from gross amount
        const netto = totalAmount / (1 + parseFloat(resolvedTaxRate.rate) / 100)
        return formatCurrency(netto)
      }
      return formatCurrency(totalAmount)
    case 'total':
      if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
        // totalAmount is already gross, so return it directly
        return formatCurrency(totalAmount)
      }
      return formatCurrency(totalAmount)
    default:
      return '-'
  }
}

const formatRepeaterFinancialValue = (repeaterItems, type) => {
  if (!Array.isArray(repeaterItems) || repeaterItems.length === 0) {
    return '-'
  }

  let total = 0
  let taxTotal = 0
  let nettoTotal = 0

  repeaterItems.forEach(item => {
    const quantity = parseFloat(item.quantity) || 0
    const unitPrice = parseFloat(item.unitPrice) || 0
    const subtotal = quantity * unitPrice
    
    // Get tax rate
    const fieldData = receipt.value?.data?.[Object.keys(receipt.value.data).find(key => 
      receipt.value.data[key] === repeaterItems
    )]
    const resolvedTaxRate = getResolvedTaxRate(item.taxRateId, fieldData)
    
    if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
      const taxAmount = subtotal * (resolvedTaxRate.rate / 100)
      const netto = subtotal
      const brutto = subtotal + taxAmount
      
      total += brutto
      taxTotal += taxAmount
      nettoTotal += netto
    } else {
      total += subtotal
      nettoTotal += subtotal
    }
  })

  switch (type) {
    case 'quantity':
      return repeaterItems.length + ' Positionen'
    case 'unitPrice':
      return '-'
    case 'taxRate':
      return taxTotal > 0 ? 'Verschiedene' : '0%'
    case 'netto':
      return formatCurrency(nettoTotal)
    case 'total':
      return formatCurrency(total)
    default:
      return '-'
  }
}

const formatSingleFinancialValue = (fieldData, type) => {
  if (!fieldData || typeof fieldData !== 'object') {
    return '-'
  }

  const quantity = parseFloat(fieldData.quantity) || 0
  const unitPrice = parseFloat(fieldData.unitPrice) || 0
  const subtotal = quantity * unitPrice

  // Get tax rate
  const resolvedTaxRate = getResolvedTaxRate(fieldData.taxRateId, fieldData)
  
  let netto = subtotal
  let tax = 0
  let total = subtotal

  if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
    tax = subtotal * (resolvedTaxRate.rate / 100)
    total = subtotal + tax
  }

  switch (type) {
    case 'quantity':
      return quantity.toString()
    case 'unitPrice':
      return formatCurrency(unitPrice)
    case 'taxRate':
      return resolvedTaxRate ? `${resolvedTaxRate.displayName} (${resolvedTaxRate.rate}%)` : '0%'
    case 'netto':
      return formatCurrency(netto)
    case 'total':
      return formatCurrency(total)
    default:
      return '-'
  }
}

const calculateFinancialSubtotal = () => {
  let subtotal = 0
  
  visibleFinancialFieldInfos.value.forEach(fieldInfo => {
    if (Array.isArray(fieldInfo.value)) {
      // Repeater field
      fieldInfo.value.forEach(item => {
        const quantity = parseFloat(item.quantity) || 0
        const unitPrice = parseFloat(item.unitPrice) || 0
        subtotal += quantity * unitPrice
      })
    } else if (typeof fieldInfo.value === 'object' && fieldInfo.value !== null) {
      if (fieldInfo.value.data !== undefined && fieldInfo.value.data !== null && fieldInfo.value.data !== '') {
        // Field object with data property - calculate total amount (gross)
        const value = parseFloat(fieldInfo.value.data) || 0
        const pricePerUnit = fieldInfo.value.pricePerUnit || null
        const totalAmount = pricePerUnit ? value * pricePerUnit : value
        subtotal += totalAmount
      } else {
        // Single field with quantity/unitPrice structure
        const quantity = parseFloat(fieldInfo.value.quantity) || 0
        const unitPrice = parseFloat(fieldInfo.value.unitPrice) || 0
        subtotal += quantity * unitPrice
      }
    } else if (typeof fieldInfo.value === 'number' || (typeof fieldInfo.value === 'string' && !isNaN(parseFloat(fieldInfo.value)))) {
      // Simple number field (like totalAmount) - this is gross
      subtotal += parseFloat(fieldInfo.value) || 0
    }
  })
  
  return subtotal
}

const calculateFinancialTaxTotal = () => {
  let taxTotal = 0
  
  visibleFinancialFieldInfos.value.forEach(fieldInfo => {
    if (Array.isArray(fieldInfo.value)) {
      // Repeater field
      fieldInfo.value.forEach(item => {
        const quantity = parseFloat(item.quantity) || 0
        const unitPrice = parseFloat(item.unitPrice) || 0
        const subtotal = quantity * unitPrice
        
        const fieldData = receipt.value?.data?.[Object.keys(receipt.value.data).find(key => 
          receipt.value.data[key] === fieldInfo.value
        )]
        const resolvedTaxRate = getResolvedTaxRate(item.taxRateId, fieldData)
        
        if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
          taxTotal += subtotal * (resolvedTaxRate.rate / 100)
        }
      })
    } else if (typeof fieldInfo.value === 'object' && fieldInfo.value !== null) {
      if (fieldInfo.value.data !== undefined && fieldInfo.value.data !== null && fieldInfo.value.data !== '') {
        // Field object with data property
        const value = parseFloat(fieldInfo.value.data) || 0
        const pricePerUnit = fieldInfo.value.pricePerUnit || null
        const totalAmount = pricePerUnit ? value * pricePerUnit : value
        
        // Get tax rate from corresponding _taxRate field
        const fieldName = fieldInfo.fieldName
        const taxRateFieldName = fieldName + '_taxRate'
        const taxRateField = receipt.value?.data?.[taxRateFieldName]
        const resolvedTaxRate = taxRateField?.resolvedTaxRate
        
        if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
          // Calculate tax from gross amount
          const netto = totalAmount / (1 + parseFloat(resolvedTaxRate.rate) / 100)
          const tax = totalAmount - netto
          taxTotal += tax
        }
      } else {
        // Single field with quantity/unitPrice
        const quantity = parseFloat(fieldInfo.value.quantity) || 0
        const unitPrice = parseFloat(fieldInfo.value.unitPrice) || 0
        const subtotal = quantity * unitPrice
        
        const resolvedTaxRate = getResolvedTaxRate(fieldInfo.value.taxRateId, fieldInfo.value)
        
        if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
          taxTotal += subtotal * (resolvedTaxRate.rate / 100)
        }
      }
    }
    // Simple number fields don't have tax information, so we skip them
  })
  
  return taxTotal
}

const calculateFinancialTotal = () => {
  // Since we're working with gross amounts, we just sum them up
  // The tax is already included in the gross amounts
  return calculateFinancialSubtotal()
}

const calculateFinancialItemTax = (value, fieldName) => {
  // Handle field objects with data property
  if (typeof value === 'object' && value !== null && value.data !== undefined) {
    const dataValue = parseFloat(value.data) || 0
    const pricePerUnit = value.pricePerUnit || null
    const totalAmount = pricePerUnit ? dataValue * pricePerUnit : dataValue
    
    // Get tax rate from corresponding _taxRate field
    const taxRateFieldName = fieldName + '_taxRate'
    const taxRateField = receipt.value?.data?.[taxRateFieldName]
    const resolvedTaxRate = taxRateField?.resolvedTaxRate
    
    if (resolvedTaxRate && resolvedTaxRate.rate > 0) {
      const netto = totalAmount / (1 + parseFloat(resolvedTaxRate.rate) / 100)
      const tax = totalAmount - netto
      return formatCurrency(tax)
    }
  }
  
  return '0,00 €'
}

const isFinancialField = (fieldName) => {
  if (!fieldName) return false
  
  // Check if field is in financial category
  const fieldData = receipt.value?.data?.[fieldName]
  if (fieldData && fieldData.category === 'financial') {
    return true
  }
  
 return false
}

// Computed properties for conditional table columns
const hasQuantityFields = computed(() => {
  return visibleFinancialFieldInfos.value.some(fieldInfo => {
    if (Array.isArray(fieldInfo.value)) {
      return fieldInfo.value.some(item => item.quantity && parseFloat(item.quantity) > 0)
    } else if (typeof fieldInfo.value === 'object' && fieldInfo.value !== null) {
      if (fieldInfo.value.data !== undefined && fieldInfo.value.data !== null && fieldInfo.value.data !== '') {
        // Field object with data property - only show quantity if it has unit and pricePerUnit
        return fieldInfo.value.unit && fieldInfo.value.pricePerUnit && parseFloat(fieldInfo.value.pricePerUnit) > 0
      }
      return fieldInfo.value.quantity && parseFloat(fieldInfo.value.quantity) > 0
    } else if (typeof fieldInfo.value === 'number' || (typeof fieldInfo.value === 'string' && !isNaN(parseFloat(fieldInfo.value)))) {
      // Simple number fields don't have quantity
      return false
    }
    return false
  }) && visibleFinancialFieldInfos.value.some(fieldInfo => {
    // Check if any field actually has a non-empty quantity value to display
    const quantityValue = formatFinancialValue(fieldInfo.value, 'quantity', fieldInfo.fieldName)
    return quantityValue && quantityValue !== '' && quantityValue !== '-'
  })
})

const hasUnitPriceFields = computed(() => {
  return visibleFinancialFieldInfos.value.some(fieldInfo => {
    if (Array.isArray(fieldInfo.value)) {
      return fieldInfo.value.some(item => item.unitPrice && parseFloat(item.unitPrice) > 0)
    } else if (typeof fieldInfo.value === 'object' && fieldInfo.value !== null) {
      if (fieldInfo.value.data !== undefined && fieldInfo.value.data !== null && fieldInfo.value.data !== '') {
        // Field object with data property - always show unit price if data exists
        return parseFloat(fieldInfo.value.data) > 0
      }
      return fieldInfo.value.unitPrice && parseFloat(fieldInfo.value.unitPrice) > 0
    } else if (typeof fieldInfo.value === 'number' || (typeof fieldInfo.value === 'string' && !isNaN(parseFloat(fieldInfo.value)))) {
      // Simple number fields are treated as having unit price
      return parseFloat(fieldInfo.value) > 0
    }
    return false
  }) && visibleFinancialFieldInfos.value.some(fieldInfo => {
    // Check if any field actually has a non-empty unit price value to display
    const unitPriceValue = formatFinancialValue(fieldInfo.value, 'unitPrice', fieldInfo.fieldName)
    return unitPriceValue && unitPriceValue !== '' && unitPriceValue !== '-'
  })
})

const hasTaxFields = computed(() => {
  return visibleFinancialFieldInfos.value.some(fieldInfo => {
    if (Array.isArray(fieldInfo.value)) {
      return fieldInfo.value.some(item => item.taxRateId)
    } else if (typeof fieldInfo.value === 'object' && fieldInfo.value !== null) {
      // Check if field has taxRateId or if there's a corresponding _taxRate field
      if (fieldInfo.value.taxRateId) {
        return true
      }
      // Check for corresponding _taxRate field
      const fieldName = fieldInfo.fieldName
      const taxRateFieldName = fieldName + '_taxRate'
      const taxRateField = receipt.value?.data?.[taxRateFieldName]
      return taxRateField && taxRateField.resolvedTaxRate
    }
    return false
  })
})

const hasNettoFields = computed(() => {
  return hasTaxFields.value
})

const hasDetailedFields = computed(() => {
  return hasQuantityFields.value || hasUnitPriceFields.value || hasTaxFields.value
})

const getColspan = () => {
  let colspan = 1 // Beschreibung
  if (hasQuantityFields.value) colspan++
  if (hasUnitPriceFields.value) colspan++
  if (hasTaxFields.value) colspan++
  if (hasNettoFields.value) colspan++
  return colspan
}

const shouldShowFinancialField = (value) => {
  // Handle field objects with data property
  if (typeof value === 'object' && value !== null && value.data !== undefined) {
    const dataValue = value.data
    // Hide if data is empty, null, undefined, or 0
    if (dataValue === '' || dataValue === null || dataValue === undefined) {
      return false
    }
    const numValue = parseFloat(dataValue)
    if (isNaN(numValue) || numValue === 0) {
      return false
    }
    return true
  }
  
  // Handle simple number values
  if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
    const numValue = parseFloat(value)
    return !isNaN(numValue) && numValue > 0
  }
  
  // Handle arrays (repeater fields)
  if (Array.isArray(value)) {
    return value.length > 0 && value.some(item => {
      if (typeof item === 'object' && item !== null) {
        const quantity = parseFloat(item.quantity) || 0
        const unitPrice = parseFloat(item.unitPrice) || 0
        return quantity > 0 || unitPrice > 0
      }
      return false
    })
  }
  
  // Handle other objects
  if (typeof value === 'object' && value !== null) {
    const quantity = parseFloat(value.quantity) || 0
    const unitPrice = parseFloat(value.unitPrice) || 0
    return quantity > 0 || unitPrice > 0
  }
  
  return false
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('de-DE')
}

const formatDateTime = (dateString) => {
  return new Date(dateString).toLocaleString('de-DE')
}

const formatTaxRateValue = (taxRateId, fieldName) => {
  if (!taxRateId) return ''
  
  
  if (typeof taxRateId === 'object' && taxRateId.displayName && taxRateId.rate) {
    return `${taxRateId.displayName} (${taxRateId.rate}%)`
  }
  
  
  return taxRateId
}

const formatRepeaterFieldValue = (repeaterItems, fieldName = null) => {
  // Handle field object with data property containing array
  let items = repeaterItems
  if (typeof repeaterItems === 'object' && repeaterItems !== null && repeaterItems.data !== undefined) {
    items = repeaterItems.data
  }
  
  if (!Array.isArray(items) || items.length === 0) {
    return 'Keine Positionen'
  }

  return items.map((item, index) => {
    const quantity = item.quantity || 0
    const unit = item.unit || ''
    const description = item.description || 'Keine Beschreibung'
    const unitPrice = parseFloat(item.unitPrice) || 0
    const taxRateId = item.taxRateId || ''
    
    // Get field data for tax rate resolution
    const fieldData = receipt.value?.data?.[fieldName]
    const resolvedTaxRate = getResolvedTaxRate(taxRateId, fieldData)
    
    // Calculate totals
    const subtotal = quantity * unitPrice
    const brutto = calculateRepeaterItemTotal(item, resolvedTaxRate)
    const netto = calculateRepeaterItemNet(item, resolvedTaxRate)
    const tax = calculateRepeaterItemTax(item, resolvedTaxRate)
    
    // Format the item
    let itemText = `${description}: ${quantity}${unit ? ' ' + unit : ''} × ${unitPrice.toFixed(2)} € = ${brutto} €`
    
    // Add tax rate info if available
    if (resolvedTaxRate) {
      itemText += ` (${resolvedTaxRate.displayName} ${resolvedTaxRate.rate}%: Netto ${netto} €, MwSt ${tax} €)`
    }
    
    return `${index + 1}. ${itemText}`
  }).join('\n')
}

const { formatFieldValue } = createReceiptFieldFormatter({
  formatCurrency,
  formatDate,
  formatDateTime,
  isFinancialField: (fieldName) => isFinancialField(fieldName),
  formatRepeaterFieldValue: (value, fieldName) => formatRepeaterFieldValue(value, fieldName),
  formatTaxRateValue: (value, fieldName) => formatTaxRateValue(value, fieldName),
})

const calculateRepeaterItemTotal = (item, taxRate = null) => {
  const quantity = parseFloat(item.quantity) || 0
  const unitPrice = parseFloat(item.unitPrice) || 0
  const subtotal = quantity * unitPrice
  
  if (taxRate && taxRate.rate) {
    // If tax rate is provided, return brutto amount
    const taxAmount = subtotal * (taxRate.rate / 100)
    return (subtotal + taxAmount).toFixed(2)
  }
  
  return subtotal.toFixed(2)
}

const calculateRepeaterItemNet = (item, taxRate = null) => {
  const quantity = parseFloat(item.quantity) || 0
  const unitPrice = parseFloat(item.unitPrice) || 0
  const subtotal = quantity * unitPrice
  
  if (taxRate && taxRate.rate) {
    const rate = parseFloat(taxRate.rate) / 100
    const netto = subtotal / (1 + rate)
    return netto.toFixed(2)
  }
  
  return subtotal.toFixed(2)
}

const calculateRepeaterItemTax = (item, taxRate = null) => {
  const quantity = parseFloat(item.quantity) || 0
  const unitPrice = parseFloat(item.unitPrice) || 0
  const subtotal = quantity * unitPrice
  
  if (taxRate && taxRate.rate) {
    const rate = parseFloat(taxRate.rate) / 100
    const netto = subtotal / (1 + rate)
    const tax = subtotal - netto
    return tax.toFixed(2)
  }
  
  return '0.00'
}

const getResolvedTaxRate = (taxRateId, fieldData = null) => {

  if (!taxRateId) return null
  // If taxRateId is already an object with rate and displayName, return it
  if (typeof taxRateId === 'object' && taxRateId.rate && taxRateId.displayName) {
    return taxRateId
  }
  
  // Try to find in field data resolved tax rates
  if (fieldData && !fieldData.type) {
    const resolvedRate = fieldData.resolvedTaxRate
    if (resolvedRate) return resolvedRate
  }
  
  if (fieldData && fieldData.resolvedTaxRates) {
    const resolvedRate = fieldData.resolvedTaxRates.find(tr => tr.id === taxRateId)
    if (resolvedRate) return resolvedRate
  }
  
  // Fallback: return a basic object with the ID
  return { id: taxRateId, rate: 19, displayName: 'MwSt' }
}

const calculateRepeaterFieldTotal = (repeaterItems, fieldName) => {
  if (!Array.isArray(repeaterItems)) return '0.00'
  
  const fieldData = receipt.value?.data?.[fieldName]
  let total = 0
  
  repeaterItems.forEach(item => {
    const resolvedTaxRate = getResolvedTaxRate(item.taxRateId, fieldData)
    const itemTotal = parseFloat(calculateRepeaterItemTotal(item, resolvedTaxRate))
    total += itemTotal
  })
  
  return total.toFixed(2)
}

const calculateRepeaterFieldNet = (repeaterItems, fieldName) => {
  if (!Array.isArray(repeaterItems)) return '0.00'
  
  const fieldData = receipt.value?.data?.[fieldName]
  let total = 0
  
  repeaterItems.forEach(item => {
    const resolvedTaxRate = getResolvedTaxRate(item.taxRateId, fieldData)
    const itemNet = parseFloat(calculateRepeaterItemNet(item, resolvedTaxRate))
    total += itemNet
  })
  
  return total.toFixed(2)
}

const calculateRepeaterFieldTax = (repeaterItems, fieldName) => {
  if (!Array.isArray(repeaterItems)) return '0.00'
  
  const fieldData = receipt.value?.data?.[fieldName]
  let total = 0
  
  repeaterItems.forEach(item => {
    const resolvedTaxRate = getResolvedTaxRate(item.taxRateId, fieldData)
    const itemTax = parseFloat(calculateRepeaterItemTax(item, resolvedTaxRate))
    total += itemTax
  })
  
  return total.toFixed(2)
}

const hasAnyUnit = (repeaterItems) => {
  if (!Array.isArray(repeaterItems)) return false
  return repeaterItems.some(item => item.unit && item.unit.trim() !== '')
}

const calculateNetAmount = (amount, taxRate) => {
  const gross = parseFloat(amount)
  const rate = parseInt(taxRate)
  const net = gross / (1 + rate / 100)
  return net.toFixed(2)
}

const calculateTaxAmount = (amount, taxRate) => {
  const gross = parseFloat(amount)
  const rate = parseInt(taxRate)
  const tax = gross - (gross / (1 + rate / 100))
  return tax.toFixed(2)
}

const getStatusLabel = (status) => {
  const labels = {
    draft: 'Entwurf',
    pending: 'Ausstehend',
    submitted: 'Eingereicht',
    approved: 'Genehmigt',
    rejected: 'Abgelehnt',
    canceled: 'Storniert',
    completed: 'Aktiv'
  }
  return labels[status] || status
}

const getPaymentStatusLabel = (status) => {
  const labels = {
    open: 'Offen',
    payed: 'Bezahlt',
    planed: 'Vorbereitet'
  }
  return labels[status] || status
}


const hasAttachments = computed(() => {
  if (!receipt.value?.attachments) return false
  
  return Object.keys(receipt.value.attachments).some(fieldName => {
    const attachments = receipt.value.attachments[fieldName]
    return Array.isArray(attachments) && attachments.length > 0
  })
})


const formattedReceiptData = computed(() => {
  if (!receipt.value?.data) return {}
  
  const result = {}
  Object.keys(receipt.value.data).forEach(key => {
    const fieldData = receipt.value.data[key]
    
    
    if (fieldData && typeof fieldData === 'object' && fieldData.label && fieldData.data !== undefined) {
      // Use fieldName + label to create unique keys for fields with same label
      const uniqueKey = `${key}_${fieldData.label}`
      result[uniqueKey] = {
        value: fieldData, // Use the entire fieldData object, not just fieldData.data
        fieldName: key
      }
    } else {
      
      result[key] = {
        value: fieldData,
        fieldName: key
      }
    }
  })
  return result
})


const formattedFieldsDataByCategory = computed(() => {
  if (!receipt.value?.data) return { personal: {}, receipt: {}, financial: {} }
  
  const categories = {
    personal: {},
    receipt: {},
    financial: {}
  }
  
  Object.keys(formattedReceiptData.value).forEach(key => {
    const fieldInfo = formattedReceiptData.value[key]
    const fieldName = fieldInfo.fieldName

    
    const originalFieldData = receipt.value.data[fieldName]
    let category = 'receipt' 
    
    if (originalFieldData && originalFieldData.category) {
      category = originalFieldData.category
    }
    if(originalFieldData?.unit) {
      fieldInfo.unit = originalFieldData.unit
    }
    if(originalFieldData?.pricePerUnit) {
      fieldInfo.pricePerUnit = originalFieldData.pricePerUnit
    }
    if (Object.prototype.hasOwnProperty.call(originalFieldData || {}, 'groupId')) {
      fieldInfo.groupId = originalFieldData.groupId
    } else if (!fieldInfo.groupId) {
      fieldInfo.groupId = null
    }
    categories[category][key] = fieldInfo
  })
  
  return categories
})

const fieldGroupsByCategory = computed(() => {
  const settingsGroups =
    receipt.value?.template?.settings?.fieldGroups ||
    receipt.value?.template?.template?.settings?.fieldGroups ||
    {}

  const normalize = (category) => {
    const groups = Array.isArray(settingsGroups?.[category]) ? settingsGroups[category] : []
    return groups
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((group) => ({
        id: group.id,
        name: group.name || '',
        color: group.color || '#E9ECEF',
      }))
      .filter((group) => group.id && group.name)
  }

  return {
    personal: normalize('personal'),
    receipt: normalize('receipt'),
    financial: normalize('financial'),
  }
})

const isTaxRateFieldName = (name) => typeof name === 'string' && name.endsWith('_taxRate')
const shouldHideField = (fieldInfo, fallbackKey) => {
  const fieldName = fieldInfo?.fieldName || fallbackKey
  return isTaxRateFieldName(fieldName)
}
const createFieldList = (fieldsObj) =>
  Object.entries(fieldsObj || {})
    .filter(([key, fieldInfo]) => fieldInfo && !shouldHideField(fieldInfo, key))
    .map(([key, fieldInfo]) => ({ key, fieldInfo }))

const personalFieldLists = computed(() =>
  groupFieldsByGroupId(
    formattedFieldsDataByCategory.value.personal,
    fieldGroupsByCategory.value.personal,
    {
      getDisplay: (fieldInfo, key) => {
        const fieldName = fieldInfo.fieldName || key
        return formatFieldValue(fieldInfo.value, fieldName)
      },
      filter: (fieldInfo, key, display) => {
        if (!fieldInfo || shouldHideField(fieldInfo, key)) return false
        return display !== '-' && display !== '' && display !== null && display !== undefined
      },
      transformField: ({ key, fieldInfo, display }) => ({ key, fieldInfo, display }),
    }
  )
)

const personalGroupedFields = computed(() => personalFieldLists.value.groups)
const personalAdditionalFields = computed(() => personalFieldLists.value.ungrouped)

const visibleFinancialFields = computed(() =>
  createFieldList(formattedFieldsDataByCategory.value.financial)
)
const visibleFinancialFieldInfos = computed(() =>
  visibleFinancialFields.value.map((item) => item.fieldInfo)
)

const isFieldInCombinedTable = (fieldName) => {
  if (!fieldName) return false
  return fieldsUsedInCombinedSet.value.has(fieldName)
}

const hasReceiptSection = computed(() => {
  if (hasCombinedRows.value) return true
  return receiptStandaloneFields.value.length > 0 || receiptGroupedFields.value.length > 0
})

const hasFinancialSection = computed(
  () => financialStandaloneFields.value.length > 0 || financialGroupedFields.value.length > 0
)

const receiptFieldLists = computed(() =>
  groupFieldsByGroupId(
    formattedFieldsDataByCategory.value.receipt,
    fieldGroupsByCategory.value.receipt,
    {
      getDisplay: (fieldInfo, key) => {
        const fieldName = fieldInfo.fieldName || key
        return formatFieldValue(fieldInfo.value, fieldName)
      },
      filter: (fieldInfo, key, display) => {
        if (!fieldInfo) return false
        const fieldName = fieldInfo.fieldName || key
        if (shouldHideField(fieldInfo, key)) return false
        if (isFieldInCombinedTable(fieldName)) return false
        return display !== '-' && display !== '' && display !== null && display !== undefined
      },
      transformField: ({ key, fieldInfo, display }) => ({ key, fieldInfo, display }),
    }
  )
)

const receiptGroupedFields = computed(() => receiptFieldLists.value.groups)
const receiptStandaloneFields = computed(() => receiptFieldLists.value.ungrouped)

const financialFieldLists = computed(() =>
  groupFieldsByGroupId(
    formattedFieldsDataByCategory.value.financial,
    fieldGroupsByCategory.value.financial,
    {
      getDisplay: (fieldInfo, key) => {
        const fieldName = fieldInfo.fieldName || key
        return formatFieldValue(fieldInfo.value, fieldName)
      },
      filter: (fieldInfo, key, display) => {
        if (!fieldInfo) return false
        const fieldName = fieldInfo.fieldName || key
        if (shouldHideField(fieldInfo, key)) return false
        if (isFieldInCombinedTable(fieldName)) return false
        if (!shouldShowFinancialField(fieldInfo.value)) return false
        return display !== '-' && display !== '' && display !== null && display !== undefined
      },
      transformField: ({ key, fieldInfo, display }) => ({ key, fieldInfo, display }),
    }
  )
)

const financialGroupedFields = computed(() => financialFieldLists.value.groups)
const financialStandaloneFields = computed(() => financialFieldLists.value.ungrouped)

const downloadAttachment = async (receiptId, fieldName, attachmentIndex, originalName) => {
  
  if (!canDownloadAttachments.value) {
    showError('Download nicht möglich - Quittung wurde abgelehnt oder storniert')
    return
  }
  
  const downloadKey = `${fieldName}-${attachmentIndex}`
  
  try {
    downloadingAttachment.value = downloadKey
    
    
    const authKey = receiptsApi.getReceiptAuthKey(receiptId)
    
    if (authKey) {
      
      await receiptsApi.downloadPublicReceiptAttachment(receiptId, fieldName, attachmentIndex, originalName)
    } else {
      
      await receiptsApi.downloadReceiptAttachment(receiptId, fieldName, attachmentIndex, originalName)
    }
    
    showSuccess('Anhang wird heruntergeladen')
  } catch (err) {
    console.error('Fehler beim Herunterladen des Anhangs:', err)
    if (err.response?.status === 403) {
      
      router.push({ 
        name: 'receipt-access', 
        params: { id: receiptId },
        query: { redirect: route.fullPath }
      })
      return
    }
    showError(err.response?.data?.error || 'Fehler beim Herunterladen des Anhangs')
  } finally {
    downloadingAttachment.value = null
  }
}


const getFileIcon = (filename) => {
  if (!filename) return 'file'
  
  const extension = filename.toLowerCase().split('.').pop()
  
  const iconMap = {
    pdf: 'file-pdf',
    doc: 'file-word',
    docx: 'file-word',
    xls: 'file-excel',
    xlsx: 'file-excel',
    ppt: 'file-powerpoint',
    pptx: 'file-powerpoint',
    txt: 'file-alt',
    jpg: 'file-image',
    jpeg: 'file-image',
    png: 'file-image',
    gif: 'file-image',
    bmp: 'file-image',
    zip: 'file-archive',
    rar: 'file-archive',
    '7z': 'file-archive',
    mp4: 'file-video',
    avi: 'file-video',
    mov: 'file-video',
    mp3: 'file-audio',
    wav: 'file-audio',
    csv: 'file-csv'
  }
  
  return iconMap[extension] || 'file'
}


const formatFileSize = (bytes) => {
  if (!bytes) return ''
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  if (bytes === 0) return '0 Bytes'
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
}

const updatePaymentStatus = async (status) => {
  try {
    let paymentDate = null
    
    if (status === 'payed') {
      paymentDate = await showPaymentDateModal()
      if (!paymentDate) {
        return 
      }
    }
    
    await receiptsApi.updateReceiptPaymentStatus([receipt.value.id], status, paymentDate)
    showSuccess('Zahlungsstatus erfolgreich aktualisiert')
    await loadReceipt() 
  } catch (err) {
    console.error('Fehler beim Aktualisieren des Zahlungsstatus:', err)
    showError(err.response?.data?.error || 'Fehler beim Aktualisieren des Zahlungsstatus')
  }
}

onMounted(async () => {
  await loadReceipt()
})
</script>

<style scoped>
/* Repeater Table Styling */
.table-responsive {
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  overflow: hidden;
  background-color: white;
}

.table {
  margin-bottom: 0;
}

.table thead th {
  border-bottom: 1px solid #dee2e6;
  font-weight: 500;
  font-size: 0.8rem;
  text-transform: none;
  letter-spacing: 0.25px;
  padding: 0.5rem 0.75rem;
  background-color: #6c757d !important;
  color: white !important;
}

.table tbody td {
  padding: 0.875rem 0.75rem;
  vertical-align: middle;
  border-top: 1px solid #dee2e6;
}

.table tfoot td {
  padding: 1rem 0.75rem;
  border-top: 2px solid #dee2e6;
  background-color: #f8f9fa !important;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 123, 255, 0.05);
}

.table-striped tbody tr:nth-of-type(odd) {
  background-color: rgba(0, 0, 0, 0.02);
}

.table-striped tbody tr:nth-of-type(odd):hover {
  background-color: rgba(0, 123, 255, 0.08);
}

/* Badge Styling */
.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

.badge.rounded-pill {
  border-radius: 50rem !important;
}

/* Wizard-ähnliche Gruppendarstellung wie im ReceiptWizard */
.wizard-field-group {
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  background: #ffffff;
  padding: 1.5rem;
}

.group-color-dot {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  display: inline-block;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.875rem;
  }
  
  .table thead th,
  .table tbody td {
    padding: 0.5rem 0.375rem;
  }
  
  .table tfoot td {
    padding: 0.75rem 0.375rem;
  }
  
  .badge {
    font-size: 0.6875rem;
    padding: 0.25rem 0.5rem;
  }
}

@media (max-width: 576px) {
  .table-responsive {
    font-size: 0.8rem;
  }
  
  .table thead th {
    font-size: 0.75rem;
    padding: 0.375rem 0.25rem;
  }
  
  .table tbody td {
    padding: 0.375rem 0.25rem;
  }
  
  .table tfoot td {
    padding: 0.5rem 0.25rem;
  }
  
  .fs-5 {
    font-size: 1rem !important;
  }
  
  .fs-6 {
    font-size: 0.875rem !important;
  }
}

/* Custom hover effects */
.table tbody tr {
  transition: all 0.2s ease-in-out;
}

.table tbody tr:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Icon spacing */
.font-awesome-icon {
  width: 0.875rem;
  height: 0.875rem;
}

/* Color enhancements */
.text-primary {
  color: #0d6efd !important;
}

.text-success {
  color: #198754 !important;
}

.text-warning {
  color: #fd7e14 !important;
}

.bg-primary {
  background-color: #0d6efd !important;
}

.bg-info {
  background-color: #0dcaf0 !important;
}

.bg-secondary {
  background-color: #6c757d !important;
}

/* Card styling with opaque rounded corners */
.card {
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

.card-body {
  background-color: white;
}
</style>
